import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router";
import withRedirect from "../../helpers/hoc/withRedirect";
import { fetchOneProperty } from "../../helpers/api/condominium";
import PageHeader from "../common/PageHeader";
import { useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Alert,
} from "reactstrap";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import ButtonIcon from "../common/ButtonIcon";
import { faDollarSign, faFileInvoiceDollar, faMoneyBillWave, faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { Toast } from "../UI/toast/Toast";
import { PreloadState } from "../UI/preloading/PreloadState";
import SupportLink from "../root/support/components/SupportLink";
import { Controller, useForm, useWatch } from "react-hook-form";
import WizardError from "../auth/register/WizardError";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import { fetchCondominiumBanks } from "../../helpers/api/banks";
import SelectBankListItem from "../common/SelectBankListItem";
import Loader from "../common/Loader";
import FullPagination from "../common/pagination/FullPagination";
import NumberFormat from "react-number-format";
import classNames from "classnames";
import { createAdvanceAdminPayment } from "../../helpers/api/finance";
import FileUploadArea from "../common/FileUploadArea";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Datetime from "react-datetime";
import moment from "moment";
import { CurrencyContext } from "../../context/Context";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import { findAllCurrencies, findAllExchangeRates } from "../../helpers/api/currency";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import {
  convertToUsd,
  convertFromUsd,
  getCurrencyName,
  formatAmountForBackend,
  formatAmountRounded,
} from "../../helpers/currencyConversion";
import CreateExhangeRateModal from "../exchange/components/CreateExchangeRateModal";
import { fetchAllPages } from "../../helpers/api/pagination";

const PropertyAdvance = ({ setRedirect, setRedirectUrl }) => {
  const { property } = useParams();
  const history = useHistory();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { role } = useSelector((state) => state.auth.user);
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const activeExchangeRate = Number(curr?.value ?? 0);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [bank, setBank] = useState(null);
  const [cash, setCash] = useState([]);
  const [cuenta, setCuenta] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [file, setFile] = useState(null);
  const [collapseOne, collapseOneOpen] = useState(false);
  const [propertyData, setData] = useState({});
  const [isLoadingProperty, setIsLoading] = useState(true);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    setError,
    clearErrors,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: {
      montoOriginal: "",
      monedaOriginal: curr?.currency?.id ? String(curr.currency.id) : "",
      payment_date: moment().format("YYYY-MM-DD"),
    },
  });

  const watchedOriginalAmount = useWatch({
    control,
    name: "montoOriginal",
  });

  const watchedOriginalCurrency = useWatch({
    control,
    name: "monedaOriginal",
    defaultValue: "",
  });

  const watchedPaymentDate = useWatch({
    control,
    name: "payment_date",
    defaultValue: moment().format("YYYY-MM-DD"),
  });

  const [isPending, setIsPending] = useState(false);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const [hasInitializedCurrencyDefault, setHasInitializedCurrencyDefault] = useState(false);

  const [params, setParams] = useState({
    condominiumId: selectedCondominium,
    search: null,
  });
  const [search, setSearch] = useState("");
  const {
    data,
    handler: { goToPage, nextPage, perPage, previousPage },
    isLoading,
    meta,
  } = useSWRFetchPagination(fetchCondominiumBanks, params);

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackUsd: false,
    }));

    const hasUsd = normalized.some((item) => item.iso === baseCurrency.iso);
    if (!hasUsd) {
      normalized.push({
        id: baseCurrency.iso,
        iso: baseCurrency.iso,
        name: getCurrencyName(baseCurrency.iso),
        isFallbackUsd: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency.iso]);

  const preferredDefaultCurrency = useMemo(() => {
    const bolivaresCurrency = currencyCatalog.find((item) => {
      const name = (item?.name || "").toLowerCase();
      const iso = (item?.iso || "").toLowerCase();
      const symbol = (item?.symbol || "").toLowerCase();

      return name.includes("boliva") || iso === "ves" || symbol === "bs" || iso === "bs";
    });

    const fallbackByBase = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    return bolivaresCurrency || fallbackByBase || currencyCatalog[0] || null;
  }, [baseCurrency.iso, currencyCatalog]);

  const selectedCurrencyData = useMemo(() => {
    const currentValue = String(watchedOriginalCurrency || "");
    return (currencyCatalog || []).find(
      (item) =>
        String(item.id) === currentValue ||
        item.iso === currentValue.toUpperCase()
    );
  }, [currencyCatalog, watchedOriginalCurrency]);

  const isSelectedCurrencyUsd =
    (selectedCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

  const parsedPaymentDate = useMemo(() => {
    if (!watchedPaymentDate) {
      return null;
    }

    const parsedDate = moment(watchedPaymentDate);
    return parsedDate.isValid() ? parsedDate : null;
  }, [watchedPaymentDate]);

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!selectedCondominium) return;

      setHasLoadedCurrencies(false);

      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: selectedCondominium,
        }));

        if (isMounted) {
          setAvailableCurrencies(response?.data || []);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setHasLoadedCurrencies(true);
        }
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [selectedCondominium]);

  useEffect(() => {
    if (!hasLoadedCurrencies || hasInitializedCurrencyDefault || !preferredDefaultCurrency) {
      return;
    }

    setValue("monedaOriginal", String(preferredDefaultCurrency.id), {
      shouldDirty: false,
      shouldValidate: true,
    });

    setHasInitializedCurrencyDefault(true);
  }, [hasLoadedCurrencies, hasInitializedCurrencyDefault, preferredDefaultCurrency, setValue]);

  const canRegisterRate = role !== "property";
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);

  const {
    rateByDate,
    rateResolution,
    isRateLoading,
    appliedExchangeRate,
    bumpRateRefreshTick,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyUsd || Boolean(selectedCurrencyData?.isFallbackUsd),
    date: parsedPaymentDate,
    activeExchangeRate,
    condominiumId: selectedCondominium,
  });

  const isSelectedDateToday =
    Boolean(parsedPaymentDate) && parsedPaymentDate.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");

  const usdEquivalent = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return null;
    }

    const convertedAmount = convertToUsd(
      watchedOriginalAmount,
      false,
      appliedExchangeRate
    );

    return convertedAmount === "" ? null : convertedAmount;
  }, [appliedExchangeRate, isSelectedCurrencyUsd, watchedOriginalAmount]);

  const helpText = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return "";
    }

    const effectiveRateDate = parsedPaymentDate?.isValid()
      ? parsedPaymentDate
      : (rateResolution?.usedDateKey ? moment(rateResolution.usedDateKey) : moment());
    const dateLabel = effectiveRateDate.format("DD/MM/YYYY");

    if (isRateLoading) {
      return "";
    }

    if (usdEquivalent === null) {
      return "";
    }

    const rateLabel = appliedExchangeRate
      ? formatAmountRounded(appliedExchangeRate, 2)
      : "N/D";

    return `Tasa ${dateLabel}: ${rateLabel} | Equivalente aproximado: ${baseCurrency.symbol}${formatAmountRounded(usdEquivalent, 2)} ${baseCurrency.iso}`;
  }, [
    appliedExchangeRate,
    isRateLoading,
    isSelectedCurrencyUsd,
    parsedPaymentDate,
    rateResolution,
    usdEquivalent,
    baseCurrency,
  ]);

  const resolveRateForCurrency = async (currencyData) => {
    if (!currencyData) {
      return null;
    }

    const isUsdCurrency =
      (currencyData?.iso || "").toUpperCase() === baseCurrency.iso ||
      currencyData?.isFallbackUsd;

    if (isUsdCurrency) {
      return 1;
    }

    try {
      const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
        currency: currencyData.id,
      }));

      const rates = response?.data || [];
      const selectedDateKey = parsedPaymentDate?.format("YYYY-MM-DD");
      const rateForDay = selectedDateKey
        ? rates
          .filter(
            (rate) =>
              moment(rate.date || rate.created_at).format("YYYY-MM-DD") ===
              selectedDateKey
          )
          .sort(
            (a, b) =>
              moment(b.date || b.created_at).valueOf() -
              moment(a.date || a.created_at).valueOf()
          )[0]
        : null;

      if (selectedDateKey && !rateForDay) {
        return 0;
      }

      return Number(rateForDay?.value || appliedExchangeRate || activeExchangeRate || 0);
    } catch (error) {
      return Number(appliedExchangeRate || activeExchangeRate || 0);
    }
  };

  const handleCurrencyChange = async (event) => {
    const nextCurrency = event.target.value;
    const nextCurrencyData = (currencyCatalog || []).find(
      (item) => String(item.id) === String(nextCurrency)
    );
    const currentCurrencyData = selectedCurrencyData;

    if (!nextCurrencyData || !currentCurrencyData) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    const currentIsUsd =
      (currentCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;
    const nextIsUsd =
      (nextCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

    if (!currentIsUsd && !nextIsUsd) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    let nextOriginalAmount = watchedOriginalAmount;

    if (currentIsUsd && !nextIsUsd) {
      const targetRate = await resolveRateForCurrency(nextCurrencyData);

      if (!targetRate || targetRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertFromUsd(
        watchedOriginalAmount,
        false,
        targetRate
      );
    } else if (!currentIsUsd && nextIsUsd) {
      const sourceRate =
        Number(appliedExchangeRate || 0) > 0 && !rateResolution?.isFallback
          ? Number(appliedExchangeRate)
          : await resolveRateForCurrency(currentCurrencyData);

      if (!sourceRate || sourceRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertToUsd(
        watchedOriginalAmount,
        false,
        sourceRate
      );
    }

    setValue("monedaOriginal", nextCurrency, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(
      "montoOriginal",
      nextOriginalAmount === "" ? "" : String(nextOriginalAmount),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  useEffect(() => {
    let isMounted = true;
    if (property) {
      (async () => {
        fetchOneProperty({ propertyId: property })
          .then((val) => {
            isMounted && setData(val.data);
          })
          .catch((error) => {
            handleErrorMessage(error);
          })
          .finally(() => {
            isMounted && setIsLoading(false);
          });
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [property]);

  const handleImageChange = (event) => {
    if (event.target.files.length !== 0) {
      setImagePreview(URL.createObjectURL(event.target.files[0]));
      register("voucher");
      setValue("voucher", event.target.files[0]);
      setFile(event.target.files[0]);
    }
  };

  const save = async (values) => {
    const { bank_id, reference, montoOriginal, payment_date } = values;
    setIsPending(true);
    try {

      if (!payment_date) {
        Toast.fire({
          type: "error",
          title: "La fecha del pago es obligatoria.",
        });
        return;
      }

      if (!isSelectedCurrencyUsd && (!appliedExchangeRate || appliedExchangeRate <= 0 || rateResolution?.isFallback)) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir esta moneda a USD.",
        });
        return;
      }

      const normalizedUsdValue = convertToUsd(
        montoOriginal,
        isSelectedCurrencyUsd,
        appliedExchangeRate
      );

      if (normalizedUsdValue === "") {
        Toast.fire({
          type: "error",
          title: "Monto invalido para convertir a USD.",
        });
        return;
      }

      const contextCurrencyIso = (curr?.currency?.iso || "").toUpperCase();
      const contextIsBaseCurrency = contextCurrencyIso === baseCurrency.iso;
      const convertedToContextCurrency = convertFromUsd(
        montoOriginal,
        contextIsBaseCurrency,
        appliedExchangeRate || Number(curr?.value ?? 0)
      );
      const amountConverted = !isSelectedCurrencyUsd
        ? montoOriginal
        : convertedToContextCurrency === ""
          ? montoOriginal
          : convertedToContextCurrency;

      const formData = new FormData();
      if (cuenta === true) {
        formData.append("bank_id", bank_id);
        formData.append("reference", reference);
      }
      formData.append("amount", formatAmountForBackend(normalizedUsdValue));
      formData.append("payment_date", payment_date);
      formData.append("amount_converted", formatAmountForBackend(amountConverted));
      formData.append("exchange_rate", appliedExchangeRate || 1);
      if (appliedExchangeRate) formData.append("rate", appliedExchangeRate);
      formData.append("property_id", property);
      if (file) formData.append("voucher", file);
      if (Number(normalizedUsdValue) > 0) {
        const { code } = await createAdvanceAdminPayment(formData, property);

        if (code === SUCCESS_RESPONSE.CREATED) {
          collapseOneOpen(false);
          setRedirectUrl(`/dashboard/properties`);
          setRedirect(true);
          Toast.fire({
            type: "success",
            title: `Saldo a favor registrado exitosamente.`,
          });
        }
      } else {
        Toast.fire({
          type: "error",
          title: `El monto del saldo a favor debe ser mayor a 0.`,
        });
      }
    } catch (error) {
      console.log(error);
      handleErrorMessage(error);
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    if (selectedBankId) {
      clearErrors("bank_id");
    } else if (cash === true) {
      setError("bank_id", {
        message: "El banco es requerido",
      });
    }
    // eslint-disable-next-line
  }, [selectedBankId]);

  const [selectionMode, setSelectionMode] = useState(null);

  const renderBanksItems = () => {
    return data.map((bank, i) => {
      return (
        <SelectBankListItem
          key={i}
          id={bank.bank_id}
          selected={selectedBankId}
          onSelect={() => {
            setBank(bank);
            setSelectedBankId(bank.bank_id);
            setValue("bank_id", bank.condominium_banks_id);
          }}
          title={bank.name}
          alternativeName="banco"
          createdAt={bank.created_at}
          owner_dni={bank.owner_dni}
          owner_name={bank.owner_name}
          account={bank.account_number}
        />
      );
    });
  };

  const renderSelectionScreen = () => (
    <Row className="justify-content-center g-3">
      <Col md={6} lg={5} className="mb-3">
        <Card
          className="h-100 cursor-pointer shadow-sm hover-shadow"
          onClick={() => setSelectionMode("advance")}
        >
          <CardBody className="d-flex flex-column align-items-center justify-content-center p-5 text-center">
            <div className="avatar avatar-5xl mb-4 d-block mx-auto">
              <div className="avatar-name rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center">
                <FontAwesomeIcon icon={faMoneyBillWave} className="fs-5" />
              </div>
            </div>
            <h4 className="mb-2">Registrar saldo a favor</h4>
            <p className="text-muted mb-0">
              Registra pagos por adelantado como saldo a favor. No cancela
              cuentas por cobrar pendientes. Para pagarlas, utiliza “Pagar
              cuentas”.
            </p>
          </CardBody>
        </Card>
      </Col>
      <Col md={6} lg={5} className="mb-3">
        <Card
          className="h-100 cursor-pointer shadow-sm hover-shadow"
          onClick={() =>
            history.push({
              pathname: `/dashboard/properties/individual-costs/create/${property}`,
              state: {
                type_id: 2,
                type_name: "Cuenta por pagar",
              },
            })
          }
        >
          <CardBody className="d-flex flex-column align-items-center justify-content-center p-5 text-center">
            <div className="avatar avatar-5xl mb-4 d-block mx-auto">
              <div className="avatar-name rounded-circle bg-soft-success text-success d-flex align-items-center justify-content-center">
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="fs-5" />
              </div>
            </div>
            <h4 className="mb-2">Crear Cuenta por Pagar</h4>
            <p className="text-muted mb-0">
              Registrar una nueva cuenta por pagar para esta propiedad.
            </p>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );

  return (
    <>
      {" "}
      <PreloadState
        loading={(isLoading && isLoadingProperty) || isPending}
        message={isPending ? "Registrando saldo a favor..." : ""}
      >
        <PageHeader
          title={`Registrar saldo a favor al propietario - ${propertyData.user?.data.firstname} ${propertyData.user?.data.lastname}`}
          titleTag="h5"
          className="mb-3"
          col={{ lg: 9 }}
        >
          <SupportLink />
        </PageHeader>
        <Row className="no-gutters">
          {!selectionMode ? (
            renderSelectionScreen()
          ) : (
            <>
              <Col className="pr-lg-2 mb-3" lg={8}>
                <Card>
                  <CardHeader>
                    <Row>
                      <Col>
                        <h5 className="mb-2 mb-md-0">Detalle del saldo a favor</h5>
                      </Col>
                      <Col xs="auto">
                        <ButtonIcon
                          color="falcon-default"
                          size="sm"
                          icon="arrow-left"
                          onClick={() => setSelectionMode(null)}
                        >
                          Volver
                        </ButtonIcon>
                      </Col>
                    </Row>
                  </CardHeader>
                  <CardBody className="bg-light">
                    <Row form>
                      <Col>
                        <Row>
                          <Col sm tag={FormGroup}>
                            <Label for="acronym">Origen de fondos</Label>
                            <Controller
                              name="payment_by_account"
                              control={control}
                              defaultValue="Selecciona"
                              rules={{
                                required: {
                                  value: true,
                                  message: "Seleccione un fondo",
                                },
                              }}
                              render={({ onChange, name, value }) => (
                                <Input
                                  type="select"
                                  name="payment_by_account"
                                  id="payment_by_account"
                                  value={value || "Selecciona"}
                                  className={`${errors.payment_by_account?.message
                                    ? "border-danger"
                                    : ""
                                    } form-select, overflow-auto`}
                                  onChange={(event) => {
                                    setCuenta(event.target.value === "1");
                                    setCash(event.target.value === "2");

                                    onChange(`${event.target.value}`);
                                  }}
                                >
                                  <option value="Selecciona" disabled>
                                    ---
                                  </option>
                                  <option value="1">Transferencia</option>
                                  <option value="2">Efectivo</option>
                                </Input>
                              )}
                            />
                            <WizardError
                              error={errors.payment_by_account?.message}
                              className="fs--1 font-weight-normal d-block"
                            />
                          </Col>
                        </Row>
                        {cuenta === true && (
                          <>
                            <Row>
                              <Col tag={FormGroup}>
                                <Label>Seleccione un banco del condominio</Label>
                                <Row>
                                  <Col tag={FormGroup} xs={9} className="pr-0">
                                    <Input
                                      type="text"
                                      placeholder="Ingrese el nombre de un banco"
                                      onChange={(event) =>
                                        setSearch(event.target.value)
                                      }
                                    />
                                  </Col>
                                  <Col tag={FormGroup} xs={3}>
                                    <ButtonIcon
                                      className="btn-block"
                                      style={{ whiteSpace: "nowrap" }}
                                      icon={faSearch}
                                      color="falcon-default"
                                      onClick={() =>
                                        setParams((prev) => {
                                          return { ...prev, search };
                                        })
                                      }
                                    >
                                      Buscar
                                    </ButtonIcon>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    {!isLoading ? (
                                      data.length >= 1 ? (
                                        renderBanksItems()
                                      ) : (
                                        <p>No se han encontrado bancos.</p>
                                      )
                                    ) : (
                                      <Loader padding="py-3" />
                                    )}
                                    <FullPagination
                                      nextPage={nextPage}
                                      prevPage={previousPage}
                                      goToPage={goToPage}
                                      meta={meta}
                                      className={"shadow-none"}
                                      posText={"bancos"}
                                      perPage={perPage}
                                    />
                                    <WizardError
                                      error={errors.bank_id?.message}
                                      className="fs--1 font-weight-normal d-block"
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                            <FormGroup>
                              <Label for="name">Nombre del banco</Label>
                              <Input
                                disabled
                                defaultValue={bank?.name || undefined}
                                autoComplete="off"
                                name="name"
                                id="title"
                                placeholder="Nombre del banco"
                              />
                            </FormGroup>

                            <Row form>
                              <Col sm tag={FormGroup}>
                                <Label for="account_number">
                                  Número de cuenta
                                </Label>
                                <NumberFormat
                                  disabled
                                  value={bank?.account_number || undefined}
                                  format="#### #### #### #### #### #### ####"
                                  placeholder="Número de cuenta"
                                  className="form-control"
                                  renderText={(value, props) => (
                                    <div {...props}>{value}</div>
                                  )}
                                />
                              </Col>

                              <Col sm tag={FormGroup}>
                                <Label for="owner_name">
                                  Nombre del propietario
                                </Label>
                                <Input
                                  disabled
                                  defaultValue={bank?.owner_name || undefined}
                                  autoComplete="off"
                                  name="owner_name"
                                  id="owner_name"
                                  placeholder="Nombre del propietario"
                                />
                              </Col>
                            </Row>

                            <Row form>
                              <Col sm tag={FormGroup}>
                                <Label for="owner_dni">
                                  Identificación (DNI)
                                </Label>
                                <Input
                                  defaultValue={bank?.owner_dni || undefined}
                                  id="owner_dni"
                                  name="owner_dni"
                                  disabled
                                  type="text"
                                  placeholder="Identificación"
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col tag={FormGroup}>
                                <FileUploadArea
                                  onChange={handleImageChange}
                                  preview={imagePreview}
                                  fileName={file?.name}
                                  onRemove={() => { setFile(null); setImagePreview(null); }}
                                  error={errors.voucher?.message}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col tag={FormGroup}>
                                <Label>Código de referencia</Label>
                                <Input
                                  className={classNames({
                                    "border-danger": errors?.reference?.message,
                                  })}
                                  type="text"
                                  name="reference"
                                  id="reference"
                                  innerRef={register({
                                    minLength: {
                                      value: 5,
                                      message:
                                        "El número de referencia debe tener al menos 5 caracteres.",
                                    },
                                  })}
                                />
                                <WizardError
                                  error={errors?.reference?.message}
                                  className="fs--1 font-weight-normal d-block"
                                />
                              </Col>
                            </Row>
                          </>
                        )}
                        {cash === true && (
                          <>
                            <Row>
                              <Col tag={FormGroup}>
                                <FileUploadArea
                                  onChange={handleImageChange}
                                  preview={imagePreview}
                                  fileName={file?.name}
                                  onRemove={() => { setFile(null); setImagePreview(null); }}
                                  error={errors.voucher?.message}
                                />
                              </Col>
                            </Row>
                          </>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col className="pl-lg-2 mb-3" lg={4}>
                <Card>
                  <CardBody className="bg-light">
                    <FormGroup>
                      <Label for="payment_date">
                        Fecha del pago <span className="text-danger">*</span>
                      </Label>
                      <Controller
                        name="payment_date"
                        control={control}
                        rules={{ required: "La fecha del pago es obligatoria." }}
                        render={({ onChange, value }) => (
                          <Datetime
                            value={value ? moment(value) : ""}
                            className={`w-100 ${errors.payment_date?.message ? "border-danger" : ""}`}
                            onChange={(date) => {
                              const parsedDate = moment(date);
                              onChange(
                                parsedDate.isValid()
                                  ? parsedDate.format("YYYY-MM-DD")
                                  : ""
                              );
                            }}
                            dateFormat="DD/MM/YYYY"
                            timeFormat={false}
                          />
                        )}
                      />
                      <WizardError
                        error={errors.payment_date?.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    </FormGroup>

                    <Row className="py-2">
                      <Col>
                        <Controller
                          name="montoOriginal"
                          control={control}
                          defaultValue=""
                          rules={{
                            required: {
                              value: true,
                              message: "Ingresar el monto",
                            },
                          }}
                          render={({ onChange, value }) => (
                            <>
                              <InputWithLeftSelect
                                stacked
                                selectLabel="Moneda del saldo a favor"
                                selectValue={watchedOriginalCurrency || ""}
                                onSelectChange={handleCurrencyChange}
                                selectOptions={(currencyCatalog || []).map((item) => ({
                                  value: String(item.id),
                                  label: `${item.iso} (${item.name})`,
                                }))}
                                errorMessage={errors.montoOriginal?.message}
                                helpText={helpText}
                                selectAriaLabel="Moneda del saldo a favor"
                              >
                                <Label className="mb-1 font-weight-semi-bold">
                                  Monto del saldo a favor
                                </Label>
                                <NumberFormat
                                  customInput={Input}
                                  thousandSeparator
                                  prefix={
                                    selectedCurrencyData?.iso
                                      ? `${selectedCurrencyData.iso} `
                                      : ""
                                  }
                                  fixedDecimalScale
                                  decimalScale={2}
                                  onValueChange={(vals) => onChange(vals.value)}
                                  value={value || ""}
                                  placeholder="0.00"
                                  className={classNames("font-weight-bold shadow-sm", {
                                    "border-danger": errors.montoOriginal,
                                  })}
                                />
                              </InputWithLeftSelect>
                              {isRateLoading && (
                                <small className="text-muted d-block mt-1">
                                  <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                                  Consultando tasa para la fecha...
                                </small>
                              )}
                              {!isSelectedCurrencyUsd && rateResolution?.isFallback && !isRateLoading && (
                                <Alert color="warning" className="mt-2 mb-0 py-2 fs--1">
                                  No se encontró la tasa oficial registrada para la fecha{" "}
                                  <strong>{moment(rateResolution.selectedDateKey).format("DD/MM/YYYY")}</strong>.
                                  {canRegisterRate ? (
                                    <>
                                      {" "}
                                      <Button
                                        color="link"
                                        className="p-0 fs--1 font-weight-bold text-decoration-none ml-1"
                                        onClick={() => setIsRateModalOpen(true)}
                                      >
                                        Registrar tasa
                                      </Button>
                                    </>
                                  ) : (
                                    " Se está utilizando una tasa de referencia."
                                  )}
                                </Alert>
                              )}
                            </>
                          )}
                        />
                      </Col>
                    </Row>

                    {Boolean(watchedOriginalAmount) && !isSelectedCurrencyUsd && (
                      <Card className="border-warning bg-soft-warning mb-3">
                        <CardBody className="py-3">
                          <div className="d-flex align-items-start">
                            <div>
                              <div className="text-700 fs--1">
                                El monto que se guardara en el sistema sera el equivalente en {getCurrencyName(baseCurrency.iso)} {baseCurrency.symbol}.
                                El valor mostrado es la referencia original ingresada por el usuario.
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    )}

                    <Alert color="info" className="fs--1">
                      Este registro sirve para ingresar saldos a favor del
                      propietario como pagos por adelantado. No se pagarán las
                      cuentas por cobrar que tenga pendientes. Para registrar
                      el pago de una cuenta por cobrar, diríjase a “Pagar
                      cuentas”.
                    </Alert>
                    <ButtonIcon
                      color="primary"
                      icon={faDollarSign}
                      onClick={() => collapseOneOpen(!collapseOne)}
                      className="btn-block"
                      disabled={isRateLoading}
                    >
                      Registrar saldo a favor
                    </ButtonIcon>
                  </CardBody>
                </Card>
              </Col>
            </>
          )}
        </Row>
        <Modal isOpen={collapseOne} data-testid="modal">
          <ModalHeader>Confirmación</ModalHeader>
          <ModalBody>
            <p className="mb-0 font-weight-bold fs--1">
              ¿Está seguro de que ingresó todos los datos correctamente? Esta
              información no puede ser cambiada posteriormente.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button disabled={isPending} onClick={() => collapseOneOpen(!collapseOne)}>
              Cancelar
            </Button>
            <Button
              data-testid="submit-button"
              color="primary"
              type="submit"
              disabled={isPending || isRateLoading}
              onClick={handleSubmit(save, (formErrors) => {
                const firstError = Object.values(formErrors)[0];
                Toast.fire({
                  type: "error",
                  title:
                    firstError?.message ||
                    "Revisa los campos obligatorios antes de continuar (banco, referencia, etc.).",
                });
              })}
            >
              {isPending && <Spinner size="sm" className="mr-2" />}
              Sí, pagar
            </Button>
          </ModalFooter>
        </Modal>
        <CreateExhangeRateModal
          isOpen={isRateModalOpen}
          setIsOpen={setIsRateModalOpen}
          currency={selectedCurrencyData || { name: "", iso: "", id: null }}
          baseCurrency={baseCurrency}
          mutate={bumpRateRefreshTick}
          condominiumId={selectedCondominium}
          suggestedValue={activeExchangeRate}
        />
      </PreloadState>
    </>
  );
};

export default withRedirect(PropertyAdvance);